import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  FileText,
  Upload,
  Download,
  Trash2,
  Plus,
  Loader2,
  Footprints,
  Building2,
  Scale,
  Heart,
  Shield,
  Zap,
  Briefcase,
  ChevronDown,
  ChevronUp,
  FolderOpen,
  File
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

// Macro aree con icone e colori
const MACRO_AREE = [
  { 
    value: "ORTOPEDIA", 
    label: "Ortopedia", 
    icon: Footprints,
    color: "bg-blue-500",
    lightColor: "bg-blue-50 dark:bg-blue-900/20",
    textColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-200 dark:border-blue-800"
  },
  { 
    value: "FINANZIARIO", 
    label: "Finanziario", 
    icon: Briefcase,
    color: "bg-green-500",
    lightColor: "bg-green-50 dark:bg-green-900/20",
    textColor: "text-green-600 dark:text-green-400",
    borderColor: "border-green-200 dark:border-green-800"
  },
  { 
    value: "CAF", 
    label: "CAF", 
    icon: Building2,
    color: "bg-purple-500",
    lightColor: "bg-purple-50 dark:bg-purple-900/20",
    textColor: "text-purple-600 dark:text-purple-400",
    borderColor: "border-purple-200 dark:border-purple-800"
  },
  { 
    value: "PATRONATO", 
    label: "Patronato", 
    icon: Heart,
    color: "bg-orange-500",
    lightColor: "bg-orange-50 dark:bg-orange-900/20",
    textColor: "text-orange-600 dark:text-orange-400",
    borderColor: "border-orange-200 dark:border-orange-800"
  },
  { 
    value: "LEGALE", 
    label: "Legale", 
    icon: Scale,
    color: "bg-red-500",
    lightColor: "bg-red-50 dark:bg-red-900/20",
    textColor: "text-red-600 dark:text-red-400",
    borderColor: "border-red-200 dark:border-red-800"
  },
  { 
    value: "ASSICURAZIONE", 
    label: "Assicurazione", 
    icon: Shield,
    color: "bg-teal-500",
    lightColor: "bg-teal-50 dark:bg-teal-900/20",
    textColor: "text-teal-600 dark:text-teal-400",
    borderColor: "border-teal-200 dark:border-teal-800"
  },
  { 
    value: "LUCE_E_GAS", 
    label: "Luce e Gas", 
    icon: Zap,
    color: "bg-yellow-500",
    lightColor: "bg-yellow-50 dark:bg-yellow-900/20",
    textColor: "text-yellow-600 dark:text-yellow-400",
    borderColor: "border-yellow-200 dark:border-yellow-800"
  }
];

const ASLMandates = ({ user }) => {
  const navigate = useNavigate();
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [newMandate, setNewMandate] = useState({
    titolo: "",
    descrizione: "",
    macro_area: "",
    file: null
  });
  
  const isAdmin = user?.role === "admin";
  const isTitolare = user?.role === "titolare";
  const canUpload = isAdmin || isTitolare;
  
  // Get visible macro areas based on user role
  const visibleAreas = isAdmin 
    ? MACRO_AREE 
    : isTitolare && user?.macro_area
      ? MACRO_AREE.filter(a => a.value === user.macro_area)
      : user?.role === "collaboratore" && Array.isArray(user?.macro_aree_visibili)
        ? MACRO_AREE.filter(a => user.macro_aree_visibili.includes(a.value))
        : MACRO_AREE;
  
  useEffect(() => {
    fetchMandates();
  }, []);
  
  const fetchMandates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/asl-mandates`, { withCredentials: true });
      setMandates(response.data);
      
      // Auto-expand areas that have mandates
      const areas = {};
      response.data.forEach(m => {
        if (m.macro_area) areas[m.macro_area] = true;
      });
      setExpandedAreas(areas);
    } catch (error) {
      toast.error("Errore nel caricamento mandati");
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpload = async () => {
    if (!newMandate.titolo || !newMandate.file || !newMandate.macro_area) {
      toast.error("Inserisci titolo, macro area e file");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append("titolo", newMandate.titolo);
    formData.append("descrizione", newMandate.descrizione);
    formData.append("macro_area", newMandate.macro_area);
    formData.append("file", newMandate.file);
    
    try {
      await axios.post(`${API}/asl-mandates`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Mandato caricato");
      setDialogOpen(false);
      setNewMandate({ titolo: "", descrizione: "", macro_area: "", file: null });
      fetchMandates();
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };
  
  const handleDownload = async (mandate) => {
    try {
      const response = await axios.get(`${API}/asl-mandates/${mandate.mandate_id}`, {
        withCredentials: true
      });
      
      // Convert base64 to blob
      const byteCharacters = atob(response.data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.content_type });
      
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (error) {
      toast.error("Errore nel download");
    }
  };
  
  const handleDelete = async (mandateId) => {
    if (!confirm("Eliminare questo mandato?")) return;
    
    try {
      await axios.delete(`${API}/asl-mandates/${mandateId}`, { withCredentials: true });
      toast.success("Mandato eliminato");
      fetchMandates();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const toggleArea = (areaValue) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaValue]: !prev[areaValue]
    }));
  };

  // Group mandates by macro_area
  const getMandatesByArea = (areaValue) => {
    return mandates.filter(m => m.macro_area === areaValue);
  };

  // Count mandates without macro_area (legacy)
  const legacyMandates = mandates.filter(m => !m.macro_area);
  
  // Open upload dialog with pre-selected macro area for titolare
  const openUploadDialog = (areaValue = null) => {
    if (isTitolare && user?.macro_area) {
      setNewMandate({ titolo: "", descrizione: "", macro_area: user.macro_area, file: null });
    } else if (areaValue) {
      setNewMandate({ titolo: "", descrizione: "", macro_area: areaValue, file: null });
    } else {
      setNewMandate({ titolo: "", descrizione: "", macro_area: "", file: null });
    }
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="asl-mandates-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Mandati
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Organizzati per macro area • {mandates.length} documenti totali
                </p>
              </div>
            </div>
            
            {canUpload && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100" data-testid="upload-mandate-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Mandato
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Carica Nuovo Mandato</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="macro_area">Macro Area *</Label>
                      {isTitolare ? (
                        <Input
                          value={MACRO_AREE.find(a => a.value === user?.macro_area)?.label || user?.macro_area}
                          disabled
                          className="mt-1.5 bg-slate-100"
                        />
                      ) : (
                        <Select
                          value={newMandate.macro_area}
                          onValueChange={(value) => setNewMandate(prev => ({ ...prev, macro_area: value }))}
                        >
                          <SelectTrigger className="mt-1.5" data-testid="mandate-macro-area-select">
                            <SelectValue placeholder="Seleziona macro area..." />
                          </SelectTrigger>
                          <SelectContent>
                            {MACRO_AREE.map((area) => {
                              const Icon = area.icon;
                              return (
                                <SelectItem key={area.value} value={area.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-5 h-5 rounded ${area.color} flex items-center justify-center`}>
                                      <Icon className="w-3 h-3 text-white" />
                                    </div>
                                    {area.label}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="titolo">Titolo *</Label>
                      <Input
                        id="titolo"
                        value={newMandate.titolo}
                        onChange={(e) => setNewMandate(prev => ({ ...prev, titolo: e.target.value }))}
                        placeholder="Es: Mandato Ortopedia - Gennaio 2025"
                        className="mt-1.5"
                        data-testid="mandate-titolo-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="descrizione">Descrizione</Label>
                      <Textarea
                        id="descrizione"
                        value={newMandate.descrizione}
                        onChange={(e) => setNewMandate(prev => ({ ...prev, descrizione: e.target.value }))}
                        placeholder="Descrizione opzionale..."
                        className="mt-1.5"
                        data-testid="mandate-descrizione-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="file">File *</Label>
                      <Input
                        id="file"
                        type="file"
                        onChange={(e) => setNewMandate(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                        className="mt-1.5"
                        data-testid="mandate-file-input"
                      />
                    </div>
                    
                    <Button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="submit-mandate-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Caricamento...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Carica Mandato
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Macro Areas with Mandates */}
            {visibleAreas.map((area) => {
              const areaMandates = getMandatesByArea(area.value);
              const Icon = area.icon;
              const isExpanded = expandedAreas[area.value];
              
              return (
                <div
                  key={area.value}
                  className={`rounded-2xl border ${area.borderColor} overflow-hidden transition-all duration-300 ${
                    isExpanded ? area.lightColor : 'bg-white dark:bg-slate-800'
                  }`}
                  data-testid={`area-section-${area.value}`}
                >
                  {/* Area Header */}
                  <button
                    onClick={() => toggleArea(area.value)}
                    className={`w-full p-4 flex items-center justify-between transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50`}
                    data-testid={`area-toggle-${area.value}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl ${area.color} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <h3 className={`font-bold ${area.textColor}`}>{area.label}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {areaMandates.length} {areaMandates.length === 1 ? 'mandato' : 'mandati'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {areaMandates.length > 0 && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${area.color} text-white`}>
                          {areaMandates.length}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Mandates List */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-700">
                      {areaMandates.length === 0 ? (
                        <div className="p-8 text-center">
                          <FolderOpen className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                          <p className="text-slate-500 dark:text-slate-400">Nessun mandato in questa area</p>
                          {canUpload && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => openUploadDialog(area.value)}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Aggiungi Mandato
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-200 dark:divide-slate-700">
                          {areaMandates.map((mandate) => (
                            <div
                              key={mandate.mandate_id}
                              className="p-4 flex items-center justify-between hover:bg-white/50 dark:hover:bg-slate-800/50 transition-colors"
                              data-testid={`mandate-item-${mandate.mandate_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                  <File className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{mandate.titolo}</p>
                                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <span>{mandate.filename}</span>
                                    <span>•</span>
                                    <span>{formatDate(mandate.created_at)}</span>
                                  </div>
                                  {mandate.descrizione && (
                                    <p className="text-xs text-slate-400 mt-1 line-clamp-1">{mandate.descrizione}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(mandate)}
                                  data-testid={`download-${mandate.mandate_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {canUpload && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(mandate.mandate_id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-${mandate.mandate_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Legacy mandates without macro_area */}
            {legacyMandates.length > 0 && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="font-semibold text-slate-700 dark:text-slate-300">Altri Mandati</h3>
                  <p className="text-xs text-slate-500">Mandati non categorizzati</p>
                </div>
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {legacyMandates.map((mandate) => (
                    <div
                      key={mandate.mandate_id}
                      className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{mandate.titolo}</p>
                          <p className="text-xs text-slate-500">{formatDate(mandate.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(mandate)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {canUpload && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(mandate.mandate_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {mandates.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Nessun mandato caricato
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {canUpload 
                    ? "Inizia caricando il primo mandato" 
                    : "Non ci sono mandati disponibili al momento"}
                </p>
                {canUpload && (
                  <Button onClick={() => openUploadDialog()} className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Carica Primo Mandato
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ASLMandates;
